/**
 * CurrencyConversionTabContent.tsx
 * Currency conversion tab content for the fee configuration editor.
 *
 * Renders the currency conversion form (edit mode) or read-only display (inherited mode).
 * Supports rate type selection with type-specific fields (markup for spotPlusMarkup,
 * fixedRate for fixed), multi-select triggers, real-time rates toggle, min/max fees,
 * threshold, currency pair token input, conversion notes, and verification section.
 *
 * This is a controlled component — the parent (FeeConfigurationEditor) owns the
 * conversion state and passes it via props. Changes are communicated via onChange.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { NotepadText, Plus, Settings2, ShieldCheck, Trash2, X } from 'lucide-react';

// Workspace packages
import {
  Select,
  SelectItem,
  NumberInput,
  Input,
  TextArea,
  Button,
  Chip,
  Card,
  CardHeader,
  CardBody,
  Popover,
  PopoverTrigger,
  PopoverContent,
  CheckboxGroup,
  Checkbox,
} from '@enfomentis/ui';
import type { Selection } from '@react-types/shared';

// Internal absolute imports (type-only)
import type { ConversionRateType, ConversionTrigger } from '@/types/shared/FeeEnums';

// Internal absolute imports
import {
  CONVERSION_RATE_TYPE_DISPLAY_MAP,
  CONVERSION_TRIGGER_DISPLAY_MAP,
  VERIFICATION_STATUS_DISPLAY_MAP,
} from '@/constants/feeEnums';
import { needsVerification } from '@/utils/feeEnums';
import { toDisplayValue, toStorageValue } from '@/utils/moneyHelpers';

// Relative imports
import type { CurrencyConversionTabContentProps, LoosePartial } from './feeEditorTypes';
import {
  VERIFICATION_STATUS_COLOR_MAP,
  formatFeeAmount,
  rescaleForPrecision,
  buildSwitchedConversion,
} from './feeEditorUtils';

// =============================================================================
// Constants
// =============================================================================

/** Currency pair format validation: 3+ letter code / 3+ letter code */
const CURRENCY_PAIR_REGEX = /^[A-Z]{3,}\/[A-Z]{3,}$/;

/** Precision dropdown options for the precision settings popover */
const PRECISION_OPTIONS = [
  { value: 0, label: '0 — (1)' },
  { value: 1, label: '1 — (0.1)' },
  { value: 2, label: '2 — (0.01)' },
  { value: 3, label: '3 — (0.001)' },
  { value: 4, label: '4 — (0.0001)' },
  { value: 5, label: '5 — (0.00001)' },
  { value: 6, label: '6 — (0.000001)' },
] as const;

// =============================================================================
// Component
// =============================================================================

export const CurrencyConversionTabContent: React.FC<CurrencyConversionTabContentProps> = ({
  conversion,
  mode,
  accountCurrency,
  onChange,
  onRemove,
  hideApplicableCurrencyPairs,
}) => {
  // ---------------------------------------------------------------------------
  // Change Helpers
  // ---------------------------------------------------------------------------

  const emitChange = useCallback(
    (partial: LoosePartial<typeof conversion>): void => {
      if (!onChange) return;
      const next: Record<string, unknown> = { ...conversion };
      for (const [key, value] of Object.entries(partial)) {
        if (value === undefined) {
          delete next[key];
        } else {
          next[key] = value;
        }
      }
      onChange(next as unknown as typeof conversion);
    },
    [conversion, onChange]
  );

  // ---------------------------------------------------------------------------
  // Select Handlers
  // ---------------------------------------------------------------------------

  const handleRateTypeChange = useCallback(
    (selection: Selection): void => {
      if (selection === 'all') return;
      const key = [...selection][0] as ConversionRateType | undefined;
      if (!key || key === conversion.rateType) return;
      if (!onChange) return;
      onChange(buildSwitchedConversion(conversion, key));
    },
    [conversion, onChange]
  );

  // ---------------------------------------------------------------------------
  // Triggers Handler
  // ---------------------------------------------------------------------------

  const handleTriggersChange = useCallback(
    (values: string[]): void => {
      emitChange({ triggers: values as ConversionTrigger[] });
    },
    [emitChange]
  );

  // ---------------------------------------------------------------------------
  // Verification Handler
  // ---------------------------------------------------------------------------

  const handleVerify = useCallback((): void => {
    emitChange({
      verificationStatus: 'userVerified',
      lastVerifiedAt: new Date().toISOString(),
    });
  }, [emitChange]);

  // ---------------------------------------------------------------------------
  // Precision Handlers
  // ---------------------------------------------------------------------------

  /** Handles rate-type-specific precision change (markup or fixedRate) */
  const handleRatePrecisionChange = useCallback(
    (newPrecision: number): void => {
      const rt = conversion.rateType;
      if (rt === 'spotPlusMarkup') {
        const oldP = conversion.markupPrecision ?? 4;
        emitChange({
          markupPrecision: newPrecision,
          markup: rescaleForPrecision(conversion.markup, oldP, newPrecision),
        });
      } else if (rt === 'fixed') {
        const oldP = conversion.fixedRatePrecision ?? 4;
        emitChange({
          fixedRatePrecision: newPrecision,
          fixedRate: rescaleForPrecision(conversion.fixedRate, oldP, newPrecision),
        });
      }
    },
    [conversion, emitChange]
  );

  /** Handles fee/threshold precision change (min/max fee + threshold together) */
  const handleFeePrecisionChange = useCallback(
    (newPrecision: number): void => {
      const changes: Record<string, unknown> = {};

      if (conversion.minFeePerTransaction !== undefined) {
        const oldP = conversion.minFeePerTransactionPrecision ?? 2;
        changes['minFeePerTransaction'] = rescaleForPrecision(conversion.minFeePerTransaction, oldP, newPrecision);
        changes['minFeePerTransactionPrecision'] = newPrecision;
      }
      if (conversion.maxFeePerTransaction !== undefined) {
        const oldP = conversion.maxFeePerTransactionPrecision ?? 2;
        changes['maxFeePerTransaction'] = rescaleForPrecision(conversion.maxFeePerTransaction, oldP, newPrecision);
        changes['maxFeePerTransactionPrecision'] = newPrecision;
      }
      if (conversion.minTradeValueThreshold !== undefined) {
        const oldP = conversion.minTradeValueThresholdPrecision ?? 2;
        changes['minTradeValueThreshold'] = rescaleForPrecision(conversion.minTradeValueThreshold, oldP, newPrecision);
        changes['minTradeValueThresholdPrecision'] = newPrecision;
      }

      emitChange(changes as LoosePartial<typeof conversion>);
    },
    [conversion, emitChange]
  );

  // ---------------------------------------------------------------------------
  // Currency Pair Token Input State
  // ---------------------------------------------------------------------------

  const [pairInput, setPairInput] = useState('');
  const [pairError, setPairError] = useState<string | null>(null);

  // Reset transient pair input state on node/config switch to prevent stale values
  useEffect(() => {
    setPairInput('');
    setPairError(null);
  }, [conversion.rateType, conversion.applicableCurrencyPairs, mode]);

  const handleAddPair = useCallback((): void => {
    const normalized = pairInput.trim().toUpperCase();
    if (!normalized) {
      setPairError('Enter a currency pair');
      return;
    }
    if (!CURRENCY_PAIR_REGEX.test(normalized)) {
      setPairError('Invalid format — use AAA/BBB (e.g. EUR/USD)');
      return;
    }
    const existing = conversion.applicableCurrencyPairs ?? [];
    if (existing.includes(normalized)) {
      setPairError('Pair already added');
      return;
    }
    emitChange({ applicableCurrencyPairs: [...existing, normalized].sort() });
    setPairInput('');
    setPairError(null);
  }, [pairInput, conversion.applicableCurrencyPairs, emitChange]);

  const handleRemovePair = useCallback(
    (pair: string): void => {
      const existing = conversion.applicableCurrencyPairs ?? [];
      const filtered = existing.filter((p) => p !== pair);
      emitChange({
        applicableCurrencyPairs: filtered.length > 0 ? filtered : undefined,
      });
    },
    [conversion.applicableCurrencyPairs, emitChange]
  );

  const handlePairInputChange = useCallback(
    (val: string): void => {
      setPairInput(val);
      if (pairError) setPairError(null);
    },
    [pairError]
  );

  const handlePairKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddPair();
      }
    },
    [handleAddPair]
  );

  // ---------------------------------------------------------------------------
  // Derived State
  // ---------------------------------------------------------------------------

  const triggers = conversion.triggers ?? [];

  const canVerify =
    needsVerification(conversion.verificationStatus) ||
    conversion.verificationStatus === 'verified';

  const currencyHint = accountCurrency ?? 'Integer amount';
  const bpsHint = 'Basis points';

  const hasRatePrecisionField =
    conversion.rateType === 'spotPlusMarkup' || conversion.rateType === 'fixed';

  const currentRatePrecision = conversion.rateType === 'fixed'
    ? (conversion.fixedRatePrecision ?? 4)
    : (conversion.markupPrecision ?? 4);

  const currentFeePrecision =
    conversion.minFeePerTransactionPrecision
    ?? conversion.maxFeePerTransactionPrecision
    ?? conversion.minTradeValueThresholdPrecision
    ?? 2;

  const hasAnyFeeField =
    conversion.minFeePerTransaction !== undefined
    || conversion.maxFeePerTransaction !== undefined
    || conversion.minTradeValueThreshold !== undefined;

  const showPrecisionPopover = hasRatePrecisionField || hasAnyFeeField;

  // ---------------------------------------------------------------------------
  // Inherited Mode (Read-Only Display)
  // ---------------------------------------------------------------------------

  if (mode === 'inherited') {
    const hasMinFee = conversion.minFeePerTransaction !== undefined;
    const hasMaxFee = conversion.maxFeePerTransaction !== undefined;
    const hasMinOrMaxFee = hasMinFee || hasMaxFee;

    return (
      <div className="flex flex-col gap-3 p-3">
        {/* ── Rate Configuration Card ── */}
        <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
          <CardHeader className="px-1 pt-0 pb-2 text-sm font-semibold text-text-primary border-b-1 border-default-200">
            Rate Configuration
          </CardHeader>
          <CardBody className="px-1 pb-0 space-y-3">
            {/* Rate Type */}
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-text-secondary">Rate Type</span>
              <span className="text-base md:text-sm text-text-primary">
                {CONVERSION_RATE_TYPE_DISPLAY_MAP[conversion.rateType]}
              </span>
            </div>

            {/* Markup (spotPlusMarkup only — no precision display) */}
            {conversion.rateType === 'spotPlusMarkup' && conversion.markup !== undefined && (
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-text-secondary">Markup</span>
                <span className="text-base md:text-sm text-text-primary">
                  {formatFeeAmount(conversion.markup, conversion.markupPrecision)} bps
                </span>
              </div>
            )}

            {/* Fixed Rate (fixed only — no precision display) */}
            {conversion.rateType === 'fixed' && conversion.fixedRate !== undefined && (
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-text-secondary">Fixed Rate</span>
                <span className="text-base md:text-sm text-text-primary">
                  {formatFeeAmount(conversion.fixedRate, conversion.fixedRatePrecision)}
                </span>
              </div>
            )}

            {/* Min/Max Fee — grid when either defined, col-span-2 for single value */}
            {hasMinOrMaxFee && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {hasMinFee && (
                  <div className={`flex flex-col gap-1 min-w-0${!hasMaxFee ? ' md:col-span-2' : ''}`}>
                    <span className="text-xs font-medium text-text-secondary">Min Fee Per Transaction</span>
                    <span className="text-base md:text-sm text-text-primary">
                      {formatFeeAmount(conversion.minFeePerTransaction, conversion.minFeePerTransactionPrecision)}
                    </span>
                  </div>
                )}
                {hasMaxFee && (
                  <div className={`flex flex-col gap-1 min-w-0${!hasMinFee ? ' md:col-span-2' : ''}`}>
                    <span className="text-xs font-medium text-text-secondary">Max Fee Per Transaction</span>
                    <span className="text-base md:text-sm text-text-primary">
                      {formatFeeAmount(conversion.maxFeePerTransaction, conversion.maxFeePerTransactionPrecision)}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Min Trade Value Threshold */}
            {conversion.minTradeValueThreshold !== undefined && (
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-text-secondary">Min Trade Value Threshold</span>
                <span className="text-base md:text-sm text-text-primary">
                  {formatFeeAmount(conversion.minTradeValueThreshold, conversion.minTradeValueThresholdPrecision)}
                </span>
              </div>
            )}

            {/* Applicable Currency Pairs */}
            {!hideApplicableCurrencyPairs && conversion.applicableCurrencyPairs && conversion.applicableCurrencyPairs.length > 0 && (
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-text-secondary">Currency Pairs</span>
                <div className="flex flex-wrap gap-1">
                  {conversion.applicableCurrencyPairs.map((pair) => (
                    <Chip key={pair} size="sm" variant="flat">{pair}</Chip>
                  ))}
                </div>
              </div>
            )}

            {/* Conversion Notes */}
            {conversion.conversionNotes && (
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-text-secondary">Conversion Notes</span>
                <span className="text-base md:text-sm text-text-primary">{conversion.conversionNotes}</span>
              </div>
            )}
          </CardBody>
        </Card>

        {/* ── Conversion Triggers Card ── */}
        <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
          <CardHeader className="px-1 pt-0 pb-2 text-sm font-semibold text-text-primary border-b-1 border-default-200">
            Conversion Triggers
          </CardHeader>
          <CardBody className="px-1 pb-0 space-y-3">
            <div className="flex flex-col gap-1">
              <span className="text-base md:text-sm text-text-primary">
                {triggers
                  .map((t) => CONVERSION_TRIGGER_DISPLAY_MAP[t])
                  .join(', ')}
              </span>
            </div>
          </CardBody>
        </Card>

        {/* ── Verification Card ── */}
        <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
          <CardHeader className="px-1 pt-0 pb-2 text-sm font-semibold text-text-primary border-b-1 border-default-200">
            Verification
          </CardHeader>
          <CardBody className="px-1 pb-0 space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-text-secondary">Status</span>
              <Chip
                size="sm"
                variant="flat"
                color={VERIFICATION_STATUS_COLOR_MAP[conversion.verificationStatus]}
              >
                {VERIFICATION_STATUS_DISPLAY_MAP[conversion.verificationStatus]}
              </Chip>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-text-secondary">Last Verified</span>
              <span className="text-base md:text-sm text-text-primary">
                {conversion.lastVerifiedAt
                  ? new Date(conversion.lastVerifiedAt).toLocaleDateString()
                  : 'Not verified'}
              </span>
            </div>
            {conversion.verificationNotes && (
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-text-secondary">Verification Notes</span>
                <span className="text-base md:text-sm text-text-primary">{conversion.verificationNotes}</span>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Edit Mode
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col gap-3 p-3">
      {/* ── Rate Configuration Card ── */}
      <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
        <CardHeader className="px-1 pt-0 pb-2 text-sm font-semibold text-text-primary border-b-1 border-default-200">
          <div className="flex items-center justify-between w-full">
            <span className="min-w-0 truncate">Rate Configuration</span>
            <div className="flex items-center gap-1 shrink-0">
              {/* Conversion Notes popover */}
              <Popover placement="bottom-end">
                <PopoverTrigger>
                  <Button
                    size="sm"
                    variant="light"
                    color={conversion.conversionNotes ? 'primary' : 'default'}
                    startContent={<NotepadText size={16} />}
                  >
                    Notes
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-3">
                  <div className="w-72 flex flex-col gap-2">
                    <p className="text-xs font-medium text-text-secondary">
                      Conversion Notes
                    </p>
                    <TextArea
                      value={conversion.conversionNotes ?? ''}
                      onValueChange={(val): void => {
                        emitChange({ conversionNotes: val || undefined });
                      }}
                      size="sm"
                      classNames={{ input: 'text-base md:text-sm' }}
                      placeholder="Notes about how this broker handles currency conversion..."
                      minRows={3}
                      maxRows={8}
                    />
                  </div>
                </PopoverContent>
              </Popover>

              {/* Precision popover */}
              {showPrecisionPopover && (
                <Popover placement="bottom-end">
                  <PopoverTrigger>
                    <Button
                      size="sm"
                      variant="light"
                      startContent={<Settings2 size={16} />}
                    >
                      Precision
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-3">
                    <div className="w-52 flex flex-col gap-3">
                      <p className="text-xs font-medium text-text-secondary">
                        Decimal Precision
                      </p>
                      {hasRatePrecisionField && (
                        <Select
                          label="Rate Precision"
                          labelPlacement="outside-top"
                          size="sm"
                          selectedKeys={[String(currentRatePrecision)]}
                          onSelectionChange={(sel): void => {
                            if (sel === 'all') return;
                            const key = [...sel][0] as string | undefined;
                            if (key !== undefined) handleRatePrecisionChange(Number(key));
                          }}
                          classNames={{ value: 'text-base md:text-sm' }}
                        >
                          {PRECISION_OPTIONS.map((opt) => (
                            <SelectItem key={String(opt.value)}>{opt.label}</SelectItem>
                          ))}
                        </Select>
                      )}
                      {hasAnyFeeField && (
                        <Select
                          label="Fee Precision"
                          labelPlacement="outside-top"
                          size="sm"
                          selectedKeys={[String(currentFeePrecision)]}
                          onSelectionChange={(sel): void => {
                            if (sel === 'all') return;
                            const key = [...sel][0] as string | undefined;
                            if (key !== undefined) handleFeePrecisionChange(Number(key));
                          }}
                          classNames={{ value: 'text-base md:text-sm' }}
                        >
                          {PRECISION_OPTIONS.map((opt) => (
                            <SelectItem key={String(opt.value)}>{opt.label}</SelectItem>
                          ))}
                        </Select>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              )}

              {/* Remove button */}
              {onRemove && (
                <Button
                  size="sm"
                  variant="light"
                  color="danger"
                  isIconOnly
                  aria-label="Remove currency conversion"
                  onPress={onRemove}
                >
                  <Trash2 size={14} />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardBody className="px-1 pb-0 space-y-3">
          {/* Rate Type Select */}
          <Select
            label="Rate Type"
            labelPlacement="outside-top"
            selectedKeys={[conversion.rateType]}
            onSelectionChange={handleRateTypeChange}
            size="sm"
            classNames={{ value: 'text-base md:text-sm' }}
          >
            {Object.entries(CONVERSION_RATE_TYPE_DISPLAY_MAP).map(([key, label]) => (
              <SelectItem key={key}>{label}</SelectItem>
            ))}
          </Select>

          {/* Markup (spotPlusMarkup — precision hidden) */}
          {conversion.rateType === 'spotPlusMarkup' && (
            <NumberInput
              label="Markup"
              labelPlacement="outside"
              value={toDisplayValue(conversion.markup, conversion.markupPrecision ?? 4)}
              onValueChange={(val): void => {
                emitChange({ markup: toStorageValue(val, conversion.markupPrecision ?? 4) });
              }}
              endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">{bpsHint}</span>}
              classNames={{ input: 'text-base md:text-sm text-right' }}
              formatOptions={{
                minimumFractionDigits: conversion.markupPrecision ?? 4,
                maximumFractionDigits: conversion.markupPrecision ?? 4,
              }}
              hideStepper
              isWheelDisabled
              size="sm"
              className="min-w-0"
            />
          )}

          {/* Fixed Rate (fixed — precision hidden) */}
          {conversion.rateType === 'fixed' && (
            <NumberInput
              label="Fixed Rate"
              labelPlacement="outside"
              value={toDisplayValue(conversion.fixedRate, conversion.fixedRatePrecision ?? 4)}
              onValueChange={(val): void => {
                emitChange({ fixedRate: toStorageValue(val, conversion.fixedRatePrecision ?? 4) });
              }}
              endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">Exchange rate</span>}
              classNames={{ input: 'text-base md:text-sm text-right' }}
              formatOptions={{
                minimumFractionDigits: conversion.fixedRatePrecision ?? 4,
                maximumFractionDigits: conversion.fixedRatePrecision ?? 4,
              }}
              hideStepper
              isWheelDisabled
              size="sm"
              className="min-w-0"
            />
          )}

          {/* TODO: Deferred until FX rate integration — usesRealTimeRates toggle + rateUpdateFrequencyMinutes */}

          {/* Min Fee + Max Fee — 2-col grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <NumberInput
              label="Min Fee Per Transaction"
              labelPlacement="outside"
              value={toDisplayValue(conversion.minFeePerTransaction, conversion.minFeePerTransactionPrecision ?? currentFeePrecision)}
              onValueChange={(val): void => {
                if (val === undefined) {
                  emitChange({ minFeePerTransaction: undefined, minFeePerTransactionPrecision: undefined });
                } else {
                  emitChange({
                    minFeePerTransaction: toStorageValue(val, conversion.minFeePerTransactionPrecision ?? currentFeePrecision),
                    ...(conversion.minFeePerTransactionPrecision === undefined ? { minFeePerTransactionPrecision: currentFeePrecision } : {}),
                  });
                }
              }}
              endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">{currencyHint}</span>}
              classNames={{ input: 'text-base md:text-sm text-right' }}
              formatOptions={{
                minimumFractionDigits: currentFeePrecision,
                maximumFractionDigits: currentFeePrecision,
              }}
              hideStepper
              isWheelDisabled
              size="sm"
              className="min-w-0"
            />
            <NumberInput
              label="Max Fee Per Transaction"
              labelPlacement="outside"
              value={toDisplayValue(conversion.maxFeePerTransaction, conversion.maxFeePerTransactionPrecision ?? currentFeePrecision)}
              onValueChange={(val): void => {
                if (val === undefined) {
                  emitChange({ maxFeePerTransaction: undefined, maxFeePerTransactionPrecision: undefined });
                } else {
                  emitChange({
                    maxFeePerTransaction: toStorageValue(val, conversion.maxFeePerTransactionPrecision ?? currentFeePrecision),
                    ...(conversion.maxFeePerTransactionPrecision === undefined ? { maxFeePerTransactionPrecision: currentFeePrecision } : {}),
                  });
                }
              }}
              endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">{currencyHint}</span>}
              classNames={{ input: 'text-base md:text-sm text-right' }}
              formatOptions={{
                minimumFractionDigits: currentFeePrecision,
                maximumFractionDigits: currentFeePrecision,
              }}
              hideStepper
              isWheelDisabled
              size="sm"
              className="min-w-0"
            />
          </div>

          {/* Min Trade Value Threshold */}
          <NumberInput
            label="Min Trade Value Threshold"
            labelPlacement="outside"
            value={toDisplayValue(conversion.minTradeValueThreshold, conversion.minTradeValueThresholdPrecision ?? currentFeePrecision)}
            onValueChange={(val): void => {
              if (val === undefined) {
                emitChange({ minTradeValueThreshold: undefined, minTradeValueThresholdPrecision: undefined });
              } else {
                emitChange({
                  minTradeValueThreshold: toStorageValue(val, conversion.minTradeValueThresholdPrecision ?? currentFeePrecision),
                  ...(conversion.minTradeValueThresholdPrecision === undefined ? { minTradeValueThresholdPrecision: currentFeePrecision } : {}),
                });
              }
            }}
            endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">{currencyHint}</span>}
            classNames={{ input: 'text-base md:text-sm text-right' }}
            formatOptions={{
              minimumFractionDigits: currentFeePrecision,
              maximumFractionDigits: currentFeePrecision,
            }}
            hideStepper
            isWheelDisabled
            size="sm"
            className="min-w-0"
          />

          {/* Applicable Currency Pairs (token input) — hidden in broker-level modal */}
          {!hideApplicableCurrencyPairs && (
            <div className="flex flex-col gap-2">
              <div className="flex gap-2 items-end">
                <Input
                  label="Currency Pairs"
                  labelPlacement="outside"
                  placeholder="e.g. EUR/USD"
                  value={pairInput}
                  onValueChange={handlePairInputChange}
                  onKeyDown={handlePairKeyDown}
                  isInvalid={pairError !== null}
                  errorMessage={pairError ?? undefined}
                  size="sm"
                  classNames={{ input: 'text-base md:text-sm' }}
                  className="flex-1 min-w-0"
                />
                <Button
                  size="sm"
                  variant="flat"
                  isIconOnly
                  onPress={handleAddPair}
                  aria-label="Add currency pair"
                >
                  <Plus size={16} />
                </Button>
              </div>
              {conversion.applicableCurrencyPairs && conversion.applicableCurrencyPairs.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {conversion.applicableCurrencyPairs.map((pair) => (
                    <Chip
                      key={pair}
                      size="sm"
                      variant="flat"
                      onClose={(): void => handleRemovePair(pair)}
                      endContent={<X size={12} />}
                    >
                      {pair}
                    </Chip>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {/* ── Conversion Triggers Card ── */}
      <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
        <CardHeader className="px-1 pt-0 pb-2 text-sm font-semibold text-text-primary border-b-1 border-default-200">
          Conversion Triggers
        </CardHeader>
        <CardBody className="px-1 pb-0 space-y-3 min-h-10">
          <CheckboxGroup
            aria-label='Triggers'
            value={triggers}
            onValueChange={handleTriggersChange}
            isRequired
            isInvalid={triggers.length === 0}
            errorMessage={triggers.length === 0 ? 'At least one trigger is required' : undefined}
            size="sm"
            orientation="horizontal"
          >
            {Object.entries(CONVERSION_TRIGGER_DISPLAY_MAP).map(([key, label]) => (
              <Checkbox key={key} value={key}>{label}</Checkbox>
            ))}
          </CheckboxGroup>
        </CardBody>
      </Card>

      {/* ── Verification Card ── */}
      <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
        <CardHeader className="px-1 pt-0 pb-2 text-sm font-semibold text-text-primary border-b-1 border-default-200">
          <div className="flex items-center justify-between w-full">
            <span>Verification</span>
            <Popover placement="bottom-end">
              <PopoverTrigger>
                <Button
                  size="sm"
                  variant="light"
                  color={conversion.verificationNotes ? 'primary' : 'default'}
                  startContent={<NotepadText size={16} />}
                >
                  Notes
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-3">
                <div className="w-72 flex flex-col gap-2">
                  <p className="text-xs font-medium text-text-secondary">
                    Verification Notes
                  </p>
                  <TextArea
                    value={conversion.verificationNotes ?? ''}
                    onValueChange={(val): void => {
                      emitChange({ verificationNotes: val || undefined });
                    }}
                    size="sm"
                    classNames={{ input: 'text-base md:text-sm' }}
                    placeholder="Notes about fee verification or discrepancies..."
                    minRows={3}
                    maxRows={8}
                  />
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </CardHeader>
        <CardBody className="px-1 pb-0 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-text-secondary">Status</span>
              <Chip
                size="sm"
                variant="flat"
                color={VERIFICATION_STATUS_COLOR_MAP[conversion.verificationStatus]}
              >
                {VERIFICATION_STATUS_DISPLAY_MAP[conversion.verificationStatus]}
              </Chip>
            </div>
            {canVerify && (
              <Button
                size="sm"
                variant="flat"
                color="success"
                startContent={<ShieldCheck size={14} />}
                onPress={handleVerify}
              >
                Verify
              </Button>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-text-secondary">Last Verified</span>
            <span className="text-base md:text-sm text-text-primary">
              {conversion.lastVerifiedAt
                ? new Date(conversion.lastVerifiedAt).toLocaleDateString()
                : 'Not verified'}
            </span>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default CurrencyConversionTabContent;
